import React from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@material-ui/core";
import ACCBTheme, { TEXT } from "../ACCBTheme";
import { formatFee, getBookingFee } from "../utils";

const themeFont = ACCBTheme.typography.fontFamily;

// ---- Booking fees (shared with BookHearingRoom) ----------------------------
// /bac/get-open-rooms/ sends full_day_fee on each room and applicable_fee on
// each slot. Anything without a fee simply shows no amount.

export const fullDayLabel = (room) => {
  const fee = formatFee(room.full_day_fee);
  return fee ? `Book full day · ${fee}` : "Book full day";
};

// A slot chip's text: the time, and its fee underneath.
export function SlotLabel({ slot }) {
  const fee = formatFee(slot.applicable_fee);
  return (
    <>
      <span>
        {formatTime12(slot.from_time)} – {formatTime12(slot.to_time)}
      </span>
      {fee && <span style={{ fontSize: "0.72rem", fontWeight: 600 }}>{fee}</span>}
    </>
  );
}

// The fee for what is picked in `room`: full-day fee or the slots' total.
export function BookingFeeSummary({ room, selectedSlotIds }) {
  const fee = room ? getBookingFee(room, selectedSlotIds) : null;
  if (!fee) return null;
  const count = selectedSlotIds.length;
  const detail = fee.fullDay ? "Full day" : `${count} slot${count === 1 ? "" : "s"}`;
  const unpricedNote = fee.unpriced
    ? ` · ${fee.unpriced} slot${fee.unpriced === 1 ? "" : "s"} without a fee not included`
    : "";
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      style={{
        gap: "1rem",
        padding: "0.6rem 1rem",
        marginBottom: "0.75rem",
        border: "1px solid #E5E5E5",
        borderRadius: "6px",
        background: "#FAFAFA",
        fontFamily: themeFont,
      }}
    >
      <Box>
        <Typography style={{ fontSize: "0.875rem", fontWeight: 600, color: TEXT.strong }}>
          Booking fee
        </Typography>
        <Typography style={{ fontSize: "0.75rem", color: TEXT.muted }}>
          {detail} · {room.room_name || `Room ${room.room_id}`}
          {/* {unpricedNote} */}
        </Typography>
        <Typography style={{ fontSize: "0.72rem", color: TEXT.muted, marginTop: "0.25rem" }}>
          Note: The invoice will be generated by the Centre.
        </Typography>
      </Box>
      <Typography style={{ fontSize: "1rem", fontWeight: 700, color: TEXT.strong, whiteSpace: "nowrap" }}>
        {formatFee(fee.amount)}
      </Typography>
    </Box>
  );
}

export const formatTime12 = (timeStr) => {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m} ${ampm}`;
};

const getRoomKey = (room) => room.room_id || room.room_name;

const isSlotFree = (slot) => !slot.booked && slot.bookable;

const getBookableSlots = (room) => (room.slots || []).filter(isSlotFree);

const canBookWholeDay = (room) => {
  const slots = room.slots || [];
  return slots.length > 0 && slots.every(isSlotFree);
};

const toSelectedSlot = (room, slot) => ({
  room_id: getRoomKey(room),
  room_name: room.room_name,
  slot_id: slot.slot_id,
  from_time: slot.from_time,
  to_time: slot.to_time,
});

/**
 * Presentational grid of hearing rooms and their time slots, as returned by
 * `/bac/get-open-rooms/` (see `searchAvailableRooms` in ../service).
 *
 * `selectedSlots` is an array of `{ room_id, slot_id, ... }` and `onChange`
 * receives the next array. Like BookHearingRoom, several slots can be picked but
 * all from the same room — picking a slot in another room starts a new selection.
 */
export default function RoomSlotPicker({ rooms, selectedSlots = [], onChange }) {
  const isSlotSelected = (room, slot) => {
    const roomKey = getRoomKey(room);
    return selectedSlots.some(
      (selected) => selected.room_id === roomKey && selected.slot_id === slot.slot_id
    );
  };

  const handleSlotToggle = (room, slot) => {
    if (!isSlotFree(slot)) return;
    const roomKey = getRoomKey(room);
    const currentRoomSlots =
      selectedSlots.length > 0 && selectedSlots[0].room_id === roomKey ? selectedSlots : [];
    const alreadySelected = currentRoomSlots.some(
      (selected) => selected.slot_id === slot.slot_id
    );
    onChange(
      alreadySelected
        ? currentRoomSlots.filter((selected) => selected.slot_id !== slot.slot_id)
        : [...currentRoomSlots, toSelectedSlot(room, slot)]
    );
  };

  const handleWholeDayToggle = (room, checked) => {
    if (checked) {
      onChange(getBookableSlots(room).map((slot) => toSelectedSlot(room, slot)));
      return;
    }
    const sameRoomSelected =
      selectedSlots.length > 0 && selectedSlots[0].room_id === getRoomKey(room);
    if (sameRoomSelected) onChange([]);
  };

  if (!rooms || rooms.length === 0) return null;

  return (
    <Box style={{ marginBottom: "1rem" }}>
      {rooms.map((room) => (
        <Box
          key={room.room_id || room.room_name}
          style={{
            marginBottom: "1rem",
            border: "1px solid #E5E5E5",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <Box
            style={{
              background: "#F5F5F5",
              padding: "0.5rem 1rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              // On a narrow screen the right-hand group wraps below the name
              // instead of squeezing it to a word per line.
              flexWrap: "wrap",
              gap: "0.25rem 1rem",
            }}
          >
            <Typography style={{ fontWeight: 600, fontSize: "0.9rem", color: TEXT.primary, flex: "1 1 8rem" }}>
              {room.room_name || `Room ${room.room_id}`}
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
              {room.booked && (
                <Typography style={{ fontSize: "0.75rem", color: TEXT.error, fontWeight: 500 }}>
                  Fully Booked
                </Typography>
              )}
              {canBookWholeDay(room) && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={getBookableSlots(room).every((slot) => isSlotSelected(room, slot))}
                      indeterminate={
                        getBookableSlots(room).some((slot) => isSlotSelected(room, slot)) &&
                        !getBookableSlots(room).every((slot) => isSlotSelected(room, slot))
                      }
                      onChange={(event) => handleWholeDayToggle(room, event.target.checked)}
                    />
                  }
                  label={fullDayLabel(room)}
                  style={{ fontSize: "0.8rem", color: TEXT.secondary, whiteSpace: "nowrap", marginRight: 0 }}
                />
              )}
              {room.seating_capacity && (
                <Typography style={{ fontSize: "0.8rem", color: TEXT.secondary, fontFamily: themeFont, whiteSpace: "nowrap" }}>
                  Seating Capacity: <strong style={{ color: TEXT.primary }}>{room.seating_capacity}</strong>
                </Typography>
              )}
            </Box>
          </Box>

          <Box style={{ padding: "0.75rem 1rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {(room.slots || []).map((slot) => {
              const isSelected = isSlotSelected(room, slot);

              // Free slots are green; booked grey; selected blue.
              let bg = "#E8F5E9";
              let border = "1px solid #81C784";
              let color = "#2e7d32";
              let cursor = "pointer";

              if (slot.booked || !slot.bookable) {
                bg = "#F5F5F5";
                border = "1px solid #E0E0E0";
                color = "#BDBDBD";
                cursor = "not-allowed";
              } else if (isSelected) {
                bg = "#EBF3FD";
                border = "1px solid #2F80ED";
                color = "#2F80ED";
              }

              return (
                <Box
                  key={slot.slot_id}
                  onClick={() => handleSlotToggle(room, slot)}
                  style={{
                    padding: "0.35rem 0.75rem",
                    borderRadius: "4px",
                    background: bg,
                    border,
                    color,
                    cursor,
                    fontSize: "0.8rem",
                    // Same weight either way: bold text is wider, so a selected
                    // chip grew and reflowed the row. The extra inset ring marks
                    // the selection without taking any space.
                    fontWeight: 400,
                    boxShadow: isSelected ? "inset 0 0 0 1px #2F80ED" : "none",
                    userSelect: "none",
                    position: "relative",
                    display: "flex",
                    // Time on top, its fee underneath.
                    flexDirection: "column",
                    alignItems: "center",
                    lineHeight: 1.35,
                    whiteSpace: "nowrap",
                    fontFamily: themeFont,
                  }}
                  title={slot.booked || !slot.bookable ? "Already booked" : ""}
                >
                  <SlotLabel slot={slot} />
                </Box>
              );
            })}
            {!(room.slots || []).length && (
              <Typography style={{ fontSize: "0.8rem", color: TEXT.muted }}>
                No slots available
              </Typography>
            )}
          </Box>
        </Box>
      ))}

      {/* Colour indicators legend */}
      <Box display="flex" style={{ gap: "1rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        <Box display="flex" alignItems="center" style={{ gap: "0.3rem" }}>
          <Box style={{ width: 12, height: 12, borderRadius: 2, background: "#E8F5E9", border: "1px solid #81C784" }} />
          <Typography style={{ fontSize: "0.75rem", color: TEXT.secondary }}>Available</Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: "0.3rem" }}>
          <Box style={{ width: 12, height: 12, borderRadius: 2, background: "#EBF3FD", border: "2px solid #2F80ED" }} />
          <Typography style={{ fontSize: "0.75rem", color: TEXT.secondary }}>Selected</Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: "0.3rem" }}>
          <Box style={{ width: 12, height: 12, borderRadius: 2, background: "#F5F5F5", border: "1px solid #E0E0E0" }} />
          <Typography style={{ fontSize: "0.75rem", color: TEXT.secondary }}>Booked</Typography>
        </Box>
      </Box>

      <BookingFeeSummary
        room={
          selectedSlots.length
            ? rooms.find((room) => getRoomKey(room) === selectedSlots[0].room_id)
            : null
        }
        selectedSlotIds={selectedSlots.map((slot) => slot.slot_id)}
      />
    </Box>
  );
}
